import { FormlyFieldConfig } from '@ngx-formly/core';

const FEATURE_TIERS = [
  { value: 'free', label: 'Free' },
  { value: 'pro', label: 'Pro' },
  { value: 'enterprise', label: 'Enterprise' },
];

const BILLING_PERIODS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual' },
];

function buildInvoicingFields(translateService, defaults: Record<string, any> = {}): FormlyFieldConfig[] {
  return [
    {
      key: 'legalName',
      type: 'input',
      defaultValue: defaults['legalName'],
      wrappers: ['default-wrapper'],
      props: { required: false },
      expressions: { 'props.label': translateService.stream('billing.config.legalName') },
    },
    {
      key: 'vatNumber',
      type: 'input',
      defaultValue: defaults['vatNumber'],
      wrappers: ['default-wrapper'],
      props: { required: false },
      expressions: { 'props.label': translateService.stream('billing.config.vatNumber') },
    },
    {
      key: 'invoiceRecipientEmail',
      type: 'input',
      defaultValue: defaults['invoiceRecipientEmail'],
      wrappers: ['default-wrapper'],
      props: { required: false, type: 'email' },
      expressions: { 'props.label': translateService.stream('billing.config.invoiceRecipientEmail') },
    },
    {
      key: 'paymentTerms',
      type: 'input',
      defaultValue: defaults['paymentTerms'],
      wrappers: ['default-wrapper'],
      props: { required: false },
      expressions: { 'props.label': translateService.stream('billing.config.paymentTerms') },
    },
    {
      key: 'notes',
      type: 'textarea',
      defaultValue: defaults['notes'],
      wrappers: ['default-wrapper'],
      props: { required: false, rows: 3 },
      expressions: { 'props.label': translateService.stream('billing.config.notes') },
    },
  ];
}

export class CreateBillingConfigForm {
  fields: FormlyFieldConfig[];
  actionText = 'button.save';

  constructor(translateService, projects: any[], companies: any[]) {
    const projectOptions = projects.map(p => ({ value: p._id, label: p.name }));
    const companyOptions = companies.map(c => ({ value: c._id, label: c.name }));

    this.fields = [
      {
        key: 'project',
        type: 'select',
        wrappers: ['default-wrapper'],
        props: { required: true, options: projectOptions },
        expressions: { 'props.label': translateService.stream('fields.project') },
      },
      {
        key: 'company',
        type: 'select',
        wrappers: ['default-wrapper'],
        props: { required: true, options: companyOptions },
        expressions: { 'props.label': translateService.stream('fields.company') },
      },
      {
        key: 'featureTier',
        type: 'select',
        defaultValue: 'free',
        wrappers: ['default-wrapper'],
        props: { required: false, options: FEATURE_TIERS },
        expressions: { 'props.label': translateService.stream('billing.config.featureTier') },
      },
      {
        key: 'billingPeriod',
        type: 'select',
        defaultValue: 'monthly',
        wrappers: ['default-wrapper'],
        props: { required: false, options: BILLING_PERIODS },
        expressions: { 'props.label': translateService.stream('billing.config.billingPeriod') },
      },
      {
        key: 'currency',
        type: 'input',
        defaultValue: 'EUR',
        wrappers: ['default-wrapper'],
        props: { required: false },
        expressions: { 'props.label': translateService.stream('billing.config.currency') },
      },
      {
        key: 'taxRate',
        type: 'input',
        defaultValue: 0,
        wrappers: ['default-wrapper'],
        props: { required: false, type: 'number', min: 0, max: 1, step: 0.01, description: '0–1 (e.g. 0.21 = 21%)' },
        expressions: { 'props.label': translateService.stream('billing.config.taxRate') },
      },
      {
        key: 'showInvoicesToOwner',
        type: 'checkbox',
        defaultValue: false,
        props: { required: false, description: 'Show invoices on the project usage page' },
        expressions: { 'props.label': translateService.stream('billing.config.showInvoicesToOwner') },
      },
      ...buildInvoicingFields(translateService),
    ];
  }
}

export class EditBillingConfigForm {
  fields: FormlyFieldConfig[];
  actionText = 'button.save';

  constructor(translateService, config: Record<string, any>) {
    this.fields = [
      {
        key: 'featureTier',
        type: 'select',
        defaultValue: config['featureTier'],
        wrappers: ['default-wrapper'],
        props: { required: false, options: FEATURE_TIERS },
        expressions: { 'props.label': translateService.stream('billing.config.featureTier') },
      },
      {
        key: 'billingPeriod',
        type: 'select',
        defaultValue: config['billingPeriod'],
        wrappers: ['default-wrapper'],
        props: { required: false, options: BILLING_PERIODS },
        expressions: { 'props.label': translateService.stream('billing.config.billingPeriod') },
      },
      {
        key: 'currency',
        type: 'input',
        defaultValue: config['currency'],
        wrappers: ['default-wrapper'],
        props: { required: false },
        expressions: { 'props.label': translateService.stream('billing.config.currency') },
      },
      {
        key: 'taxRate',
        type: 'input',
        defaultValue: config['taxRate'],
        wrappers: ['default-wrapper'],
        props: { required: false, type: 'number', min: 0, max: 1, step: 0.01, description: '0–1 (e.g. 0.21 = 21%)' },
        expressions: { 'props.label': translateService.stream('billing.config.taxRate') },
      },
      {
        key: 'showInvoicesToOwner',
        type: 'checkbox',
        defaultValue: config['showInvoicesToOwner'],
        props: { required: false, description: 'Show invoices on the project usage page' },
        expressions: { 'props.label': translateService.stream('billing.config.showInvoicesToOwner') },
      },
      ...buildInvoicingFields(translateService, config),
    ];
  }
}
